import { useEffect, useId, useRef, type ReactNode } from 'react';
import { cn } from './cn.ts';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusableWithin(panel: HTMLElement): HTMLElement[] {
  return [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)];
}

/**
 * What should hold focus when the dialog opens.
 *
 * Order matters, and a single `querySelector` with a comma-separated list
 * cannot express it: that returns the first match in *document* order, not
 * the first matching selector. The dismiss button sits in the header, above
 * every field, so it always won — opening any form dialog put the caret on
 * the close button rather than the first input.
 */
function firstFocusTarget(panel: HTMLElement | null): HTMLElement | null {
  if (!panel) return null;

  const requested = panel.querySelector<HTMLElement>('[data-autofocus]');
  if (requested) return requested;

  const candidates = focusableWithin(panel).filter(
    (element) => !element.hasAttribute('data-modal-dismiss'),
  );

  // A field first, so typing works immediately; otherwise whatever the dialog
  // does offer, and the dismiss button only when it offers nothing else.
  return (
    candidates.find((element) => element.matches('input, select, textarea')) ??
    candidates[0] ??
    panel.querySelector<HTMLElement>('[data-modal-dismiss]')
  );
}

export interface ModalProps {
  readonly open: boolean;
  readonly title: string;
  readonly subtitle?: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  readonly widthClassName?: string;
  /**
   * Whether the dialog can be dismissed without acting on it.
   *
   * True everywhere but one place. The safety notice (SCRUM-208) is false:
   * Escape, the backdrop and the close button are all gone, and the only
   * way out is the acknowledgement. That is the difference between the
   * notice and a cookie banner, and it is the entire reason the feature
   * was asked for.
   *
   * It does **not** disable the focus trap, the labelled dialog or the
   * inert background — which is why this is a flag here rather than a
   * bespoke dialog at the call site. Reimplementing those is how a modal
   * ends up unreachable by keyboard.
   */
  readonly dismissible?: boolean;
}

/**
 * The shell every record opens in.
 *
 * Handles the accessibility behaviour that is easy to forget and awkward to
 * retrofit: labelled dialog, focus moved in on open and restored on close,
 * focus trapped while open, Escape to dismiss, and the background made inert
 * to screen readers.
 */
export function Modal({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer,
  widthClassName = 'max-w-2xl',
  dismissible = true,
}: ModalProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  /**
   * Held in a ref so the effect below can depend on `open` alone.
   *
   * Every caller passes a fresh closure for `onClose` — an inline arrow, or a
   * function declared in the component body — so depending on it directly
   * tore the effect down and set it up again on *every* render, including the
   * one caused by typing a single character. Focus was pulled back to the top
   * of the dialog on each keystroke, which made the form impossible to fill
   * in.
   */
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;

    firstFocusTarget(panelRef.current)?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Swallowed either way: an undismissible dialog must not let
        // Escape reach whatever is behind it and close *that* instead.
        event.stopPropagation();
        if (dismissible) onCloseRef.current();
        return;
      }
      // Read the panel fresh rather than capturing it when the effect ran:
      // the dialog's contents change as the form does, and a stale element
      // would trap focus against a list of controls that no longer exists.
      const panel = panelRef.current;
      if (event.key !== 'Tab' || !panel) return;

      const focusable = focusableWithin(panel);
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable.at(-1)!;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = overflow;
      previouslyFocused.current?.focus();
    };
  }, [open, dismissible]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div
        className="fixed inset-0 bg-slate-900/40"
        onClick={dismissible ? onClose : undefined}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          'relative w-full rounded-xl bg-white shadow-xl ring-1 ring-slate-200',
          widthClassName,
        )}
      >
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 p-5">
          <div>
            {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
            <h2 id={titleId} className="text-lg font-semibold text-slate-900">
              {title}
            </h2>
          </div>
          {dismissible && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              data-modal-dismiss=""
              className="rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-600"
            >
              <svg viewBox="0 0 20 20" className="h-5 w-5" fill="currentColor" aria-hidden="true">
                <path d="M6.3 6.3a1 1 0 011.4 0L10 8.6l2.3-2.3a1 1 0 111.4 1.4L11.4 10l2.3 2.3a1 1 0 01-1.4 1.4L10 11.4l-2.3 2.3a1 1 0 01-1.4-1.4L8.6 10 6.3 7.7a1 1 0 010-1.4z" />
              </svg>
            </button>
          )}
        </header>

        <div className="p-5">{children}</div>

        {footer && (
          <footer className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 p-4">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}
